import React, { useEffect, useState, Fragment } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Switch,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { useHistory } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend } from "../../api";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedPaymentMethod } from "../../redux-store/auth/auth";
import { styled } from "@mui/system";
import { AccountBalance } from "@mui/icons-material";

const StyledSelect = styled(Select)({
  height: "36px",
  minWidth: "120px",
  fontSize: "14px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  padding: "0 8px",
});

const PaymentMethod = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [disableAddNew, setDisableAddNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useSelector((state) => state.auth);
  const history = useHistory();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("verified");

  const [value, setValue] = useState("e-money");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const fetchPaymentMethods = async (filterType = "verified") => {
    setLoading(true);
    try {
      const response = await backend.get(
        `/payment-method?user=${currentUser.author._id}`
      );

      const methods = response.data.paymentMethods.methods || [];
      let filteredMethods = methods;

      // Apply filter based on the filterType parameter
      if (filterType === "verified") {
        filteredMethods = methods.filter((method) => method.verified);
      } else if (filterType === "unverified") {
        filteredMethods = methods.filter((method) => !method.verified);
      }

      setPaymentMethods(filteredMethods);
      setDefaultPaymentMethod(
        response.data.paymentMethods.selectedAccountNo || null
      );

      const hasBankAccount = filteredMethods.some(
        (method) => method.receivingType === "bank_account"
      );
      const hasEMoney = filteredMethods.some(
        (method) => method.receivingType === "e_money"
      );
      setDisableAddNew(hasBankAccount && hasEMoney);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      toast.error("Failed to fetch payment methods.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.author?._id) {
      fetchPaymentMethods(filter);
    }
  }, [currentUser, filter]);

  const handleDefaultSwitchChange = async (method) => {
    if (!method.verified) {
      toast.error("Only verified payment methods can be set as default.");
      return;
    }

    const selectedAccountNo =
      method.receivingType === "bank_account"
        ? method.stripeConnectId
        : method.accountNo;

    try {
      const response = await backend.post("/payment-method", {
        user: currentUser.author._id,
        selectedPaymentType: method.receivingType,
        selectedAccountNo,
      });

      setDefaultPaymentMethod(method.receivingType);
      dispatch(setSelectedPaymentMethod(method.receivingType));
      fetchPaymentMethods();

      toast.success(
        response.data.message || "Default payment method updated successfully."
      );
    } catch (error) {
      console.error("Error updating default payment method:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update default payment method."
      );
    }
  };

  const handleDelete = async (method) => {
    try {
      const deleteUrl =
        method.receivingType === "bank_account"
          ? `stripe-connect/delete/${method._id}/${method.stripeConnectId}`
          : `stripe-connect/delete/${method._id}/${method.accountNo}`;

      const response = await backend.delete(deleteUrl);
      toast.success(
        response.data.message || "Payment method deleted successfully."
      );
      fetchPaymentMethods();
    } catch (error) {
      console.error("Error deleting payment method:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete payment method."
      );
    }
  };

  const handleViewDetails = (method) => {
    history.push(`/payment-method-details/${method._id}`);
  };

  const filteredMethods = paymentMethods.filter(
    (method) =>
      (value === "e-money" && method.receivingType === "e_money") ||
      (value === "connect-account" && method.receivingType === "bank_account")
  );

  return (
    <Fragment>
      <ToastContainer />
      <Box style={{ width: "80%", maxWidth: 800, margin: "50px auto" }}>
        <Typography variant="h6" style={{ marginBottom: 20 }}>
          Manage Payment Methods
        </Typography>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{ marginBottom: 20 }}
        >
          <Tabs value={value} onChange={handleChange}>
            <Tab value="e-money" label="E-money Account" />
            <Tab value="connect-account" label="Connected Account" />
          </Tabs>

          <Box display="flex" alignItems="center" gap={1}>
            <StyledSelect
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="verified">Completed</MenuItem>
              <MenuItem value="unverified">In Progress</MenuItem>
            </StyledSelect>

            <Button
              variant="contained"
              onClick={() =>
                history.push(
                  `/add-new-payment-method?method=${
                    value === "e-money" ? "e_money" : "bank_account"
                  }`
                )
              }
            >
              Add New
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <ClipLoader size={36} color="#123abc" loading={loading} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account No</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMethods.length > 0 ? (
                  filteredMethods.map((method) => (
                    <TableRow key={method._id}>
                      <TableCell>
                        {method.receivingType === "bank_account" ? (
                          method.accountNo ? (
                            method.accountNo
                          ) : method.additionalInfo?.data?.[0]
                              ?.routing_number &&
                            method.additionalInfo?.data?.[0]?.last4 ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <AccountBalance
                                style={{ color: "#123abc", fontSize: 24 }}
                              />
                              <Box>
                                <Typography variant="body1">
                                  {method.additionalInfo?.data?.[0]
                                    ?.bank_name || "Unknown Bank"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>
                                    {
                                      method.additionalInfo?.data?.[0]
                                        ?.routing_number
                                    }
                                  </strong>
                                  {" •••• "}
                                  <strong>
                                    {method.additionalInfo?.data?.[0]?.last4}
                                  </strong>
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography color="error">
                              No Bank Account
                            </Typography>
                          )
                        ) : (
                          method.accountNo || "N/A"
                        )}
                      </TableCell>

                      <TableCell>
                        {method.receivingType === "bank_account"
                          ? "Bank Account"
                          : "E-money"}
                      </TableCell>

                      <TableCell>
                        <Switch
                          checked={
                            method.receivingType === "bank_account"
                              ? method.stripeConnectId === defaultPaymentMethod
                              : method.accountNo === defaultPaymentMethod
                          }
                          onChange={() => handleDefaultSwitchChange(method)}
                          disabled={!method.verified}
                        />
                      </TableCell>

                      <TableCell>
                        {method.verified ? (
                          "Completed"
                        ) : (
                          <Typography
                            color="primary"
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            onClick={() => {
                              if (value === "e-money") {
                                handleViewDetails(method);
                              } else {
                                history.push(
                                  `/add-new-payment-method?method=${
                                    value === "e-money"
                                      ? "e_money"
                                      : "bank_account"
                                  }&stripeaccountid=${method.stripeConnectId}`
                                );
                              }
                            }}
                          >
                            Click Here to Continue
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton onClick={() => handleViewDetails(method)}>
                            <VisibilityIcon color="primary" />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(method)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No {value === "e-money" ? "E-money" : "Bank"} accounts
                      exist.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Fragment>
  );
};

export default PaymentMethod;
